import React, { useMemo, useState } from "react";
import "./PunchCardPage.css";

// Very small toy language inspired by 70s BASIC, executed line-by-line.
// Supported instructions (case-insensitive):
//  - REM comment text
//  - LET var = number
//  - ADD var, number
//  - PRINT expression (var or number)
//  - GOTO lineNumber
// Line numbers are implicit by order (1-based). GOTO uses those numbers.

const MAX_COLUMNS = 80; // IBM 80-column card

function sanitizeToCardColumns(text) {
  const trimmed = (text ?? "").toString();
  if (trimmed.length === MAX_COLUMNS) return trimmed;
  if (trimmed.length < MAX_COLUMNS) return trimmed.padEnd(MAX_COLUMNS, " ");
  return trimmed.slice(0, MAX_COLUMNS);
}

function parseProgramToCards(source) {
  // Split by lines, ensure each is 80 columns; empty program yields one blank card
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  if (lines.length === 0) return [sanitizeToCardColumns("")];
  return lines.map((line) => sanitizeToCardColumns(line));
}

function tokenizeInstruction(raw) {
  const line = raw.trim();
  if (line.length === 0) return { op: "REM", args: [""] };
  const upper = line.toUpperCase();
  if (upper.startsWith("REM"))
    return { op: "REM", args: [line.slice(3).trim()] };
  if (upper.startsWith("LET ")) {
    // LET A = 10
    const m = /LET\s+([A-Z][A-Z0-9_]*)\s*=\s*(-?\d+(?:\.\d+)?)\s*$/i.exec(line);
    if (!m) throw new Error("Invalid LET syntax. Use: LET VAR = number");
    return { op: "LET", args: [m[1], Number(m[2])] };
  }
  if (upper.startsWith("ADD ")) {
    // ADD A, 5
    const m = /ADD\s+([A-Z][A-Z0-9_]*)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/i.exec(line);
    if (!m) throw new Error("Invalid ADD syntax. Use: ADD VAR, number");
    return { op: "ADD", args: [m[1], Number(m[2])] };
  }
  if (upper.startsWith("PRINT ")) {
    // PRINT X | PRINT 123
    const mVar = /PRINT\s+([A-Z][A-Z0-9_]*)\s*$/i.exec(line);
    if (mVar) return { op: "PRINT_VAR", args: [mVar[1]] };
    const mNum = /PRINT\s+(-?\d+(?:\.\d+)?)\s*$/i.exec(line);
    if (mNum) return { op: "PRINT_NUM", args: [Number(mNum[1])] };
    throw new Error("Invalid PRINT syntax. Use: PRINT VAR | PRINT number");
  }
  if (upper.startsWith("GOTO ")) {
    const m = /GOTO\s+(\d+)\s*$/i.exec(line);
    if (!m) throw new Error("Invalid GOTO syntax. Use: GOTO lineNumber");
    return { op: "GOTO", args: [Number(m[1])] };
  }
  throw new Error("Unknown instruction: " + line);
}

function interpretStep(state) {
  const { cards, pc, variables, output } = state;
  if (pc < 1 || pc > cards.length) {
    return { ...state, halted: true, note: "Program counter out of range" };
  }
  const raw = cards[pc - 1];
  const text = raw.replace(/\s+$/g, "");
  let nextPc = pc + 1;
  let halted = false;
  let note = "";
  const newVars = { ...variables };
  const newOutput = [...output];

  try {
    const instr = tokenizeInstruction(text);
    switch (instr.op) {
      case "REM":
        note = "Comment";
        break;
      case "LET": {
        const [name, value] = instr.args;
        newVars[name] = value;
        note = `LET ${name} = ${value}`;
        break;
      }
      case "ADD": {
        const [name, value] = instr.args;
        const current = Number(newVars[name] ?? 0);
        newVars[name] = current + value;
        note = `ADD ${name}, ${value} -> ${newVars[name]}`;
        break;
      }
      case "PRINT_VAR": {
        const [name] = instr.args;
        const v = newVars[name] ?? 0;
        newOutput.push(String(v));
        note = `PRINT ${name} (${v})`;
        break;
      }
      case "PRINT_NUM": {
        const [num] = instr.args;
        newOutput.push(String(num));
        note = `PRINT ${num}`;
        break;
      }
      case "GOTO": {
        const [lineNumber] = instr.args;
        if (lineNumber < 1 || lineNumber > cards.length) {
          throw new Error("GOTO target out of range");
        }
        nextPc = lineNumber;
        note = `GOTO ${lineNumber}`;
        break;
      }
      default:
        throw new Error("Unsupported instruction");
    }
  } catch (e) {
    halted = true;
    note = `Error: ${e.message}`;
  }

  const reachedEnd = nextPc > cards.length;
  return {
    ...state,
    pc: reachedEnd ? nextPc : nextPc,
    variables: newVars,
    output: newOutput,
    halted: halted || reachedEnd,
    note,
    lastExecuted: pc,
  };
}

export default function PunchCardPage() {
  const defaultProgram = useMemo(
    () =>
      [
        "REM Example program",
        "LET A = 1",
        "ADD A, 4",
        "PRINT A",
        "GOTO 6",
        "PRINT 999",
        "PRINT 42",
      ].join("\n"),
    []
  );

  const [programText, setProgramText] = useState(defaultProgram);
  const [cards, setCards] = useState(() => parseProgramToCards(defaultProgram));
  const [state, setState] = useState({
    cards,
    pc: 1, // 1-based
    variables: {},
    output: [],
    halted: false,
    note: "",
    lastExecuted: 0,
  });

  function handleCompile() {
    const newCards = parseProgramToCards(programText);
    setCards(newCards);
    setState({
      cards: newCards,
      pc: 1,
      variables: {},
      output: [],
      halted: false,
      note: "Compiled",
      lastExecuted: 0,
    });
  }

  function handleStep() {
    if (state.halted) return;
    setState((prev) => interpretStep(prev));
  }

  function handleRun() {
    let running = { ...state };
    let safety = 5000; // prevent infinite loops
    while (!running.halted && safety-- > 0) {
      running = interpretStep(running);
    }
    setState(running);
  }

  return (
    <div className="container">
      <div className="punch-wrap">
        <h1 className="punch-title">IBM 80-Column Punch Card Simulator</h1>
        <div className="punch-layout">
          <div className="punch-left">
            <label className="punch-label">
              Program (one instruction per card line)
            </label>
            <textarea
              className="punch-editor"
              value={programText}
              onChange={(e) => setProgramText(e.target.value)}
              spellCheck={false}
            />

            <div className="punch-controls">
              <button className="pbtn" onClick={handleCompile}>
                Compile
              </button>
              <button
                className="pbtn"
                onClick={handleStep}
                disabled={state.halted}
              >
                Step
              </button>
              <button
                className="pbtn"
                onClick={handleRun}
                disabled={state.halted}
              >
                Run
              </button>
            </div>

            <div className="punch-io">
              <div>
                <div className="punch-subtitle">Output</div>
                <div className="punch-output">
                  {state.output.map((line, idx) => (
                    <div key={idx} className="punch-output-line">
                      {line}
                    </div>
                  ))}
                  {state.output.length === 0 && (
                    <div className="punch-output-line dim">(no output)</div>
                  )}
                </div>
              </div>
              <div>
                <div className="punch-subtitle">Registers</div>
                <div className="punch-vars">
                  {Object.keys(state.variables).length === 0 && (
                    <div className="punch-output-line dim">(empty)</div>
                  )}
                  {Object.entries(state.variables).map(([k, v]) => (
                    <div key={k} className="punch-var-row">
                      <span className="punch-var-name">{k}</span>
                      <span className="punch-var-val">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="punch-status">
              <div>
                <b>PC</b>: {state.pc}
              </div>
              <div>
                <b>Last</b>: {state.lastExecuted || "-"}
              </div>
              <div>
                <b>Status</b>: {state.halted ? "HALTED" : "READY"}
              </div>
              <div className="note">{state.note}</div>
            </div>
          </div>

          <div className="punch-right">
            <div className="punch-subtitle">Card Deck</div>
            <div className="card-deck">
              {cards.map((card, idx) => {
                const isActive = state.pc === idx + 1 && !state.halted;
                const isExecuted = state.lastExecuted === idx + 1;
                return (
                  <div
                    key={idx}
                    className={`card ${isActive ? "active" : ""} ${
                      isExecuted ? "executed" : ""
                    }`}
                  >
                    <div className="card-top">
                      <span className="card-index">
                        {String(idx + 1).padStart(3, "0")}
                      </span>
                      <span className="card-holes-label">80 COL</span>
                    </div>
                    <div className="card-body">
                      <div className="card-text">{card}</div>
                      <div className="holes">
                        {card.split("").map((ch, ci) => (
                          <div
                            key={ci}
                            className={`hole ${ch !== " " ? "punched" : ""}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
